import * as API from '@omniflow/omni-webapi';
import { CalendarType } from '@omniflow/omni-webapi';
import moment from 'moment';
import { differenceInDays } from 'date-fns';
import { uniqBy } from 'lodash';
import differenceInCalendarDays from 'date-fns/esm/fp/differenceInCalendarDays/index';

import { CalendarHelper } from '../helpers/calendar-helper';

import { Period, PeriodUnit } from './period-helper';

/**
 * Column names used for fiscal period data extraction
 */
const FISCAL_PERIOD_COLUMNS = {
    MONTHLYFISCAL: 'period6_text',
    QUARTERLYFISCAL: 'clientquarter',
    FIRST_DAY: 'firstday',
    LAST_DAY: 'lastday'
}

/**
 * Represents a fiscal period block with metadata
 */
interface FiscalPeriodBlock {
    value: string;
    length: number;
    startDate: Date;
    endDate: Date;
}

/**
 * Represents calendar data entry structure
 */
interface CalendarDataEntry {
    ColumnName: string;
    JsonValue: string;
}

/**
 * Result of extracting column data from calendar entry
 */
interface ExtractedColumnData {
    periodValue?: string;
    firstDay?: string;
    lastDay?: string;
}

/**
 * Safely parses JSON value and validates it's not null
 * @param jsonValue - The JSON string to parse
 * @returns Parsed value or undefined if parsing fails or value is null
 */
const parseJsonValue = (jsonValue: string): any => {
    try {
        const parsed = JSON.parse(jsonValue);
        return parsed === null ? undefined : parsed;
    } catch (error) {
        // Silently handle parsing errors
        return undefined;
    }
};

/**
 * Safely parses date from JSON value using moment.js
 * @param jsonValue - The JSON string containing date
 * @returns Valid Date object or null if parsing fails
 */
const parseDateFromJson = (jsonValue: string): Date | null => {
    const parsed = parseJsonValue(jsonValue);
    if (!parsed || typeof parsed !== 'string') {
        return null;
    }

    const date = moment.utc(parsed);
    return date.isValid() ? date.toDate() : null;
};

/**
 * Extracts required column data from a calendar data entry
 * Uses early exit optimization for better performance
 * @param dataEntry - Array of calendar data entries
 * @returns Object containing extracted column values
 */
const extractColumnData = (dataEntry: CalendarDataEntry[], periodUnit: PeriodUnit): ExtractedColumnData => {
    const result: ExtractedColumnData = {};

    // Use for-of loop for better performance and early exit capability
    for (const entry of dataEntry) {
        switch (entry.ColumnName) {
            case FISCAL_PERIOD_COLUMNS[periodUnit]:
                result.periodValue = entry.JsonValue;
                break;
            case FISCAL_PERIOD_COLUMNS.FIRST_DAY:
                result.firstDay = entry.JsonValue;
                break;
            case FISCAL_PERIOD_COLUMNS.LAST_DAY:
                result.lastDay = entry.JsonValue;
                break;
        }

        // Early exit if all required data found
        if (result.periodValue && result.firstDay && result.lastDay) {
            break;
        }
    }

    return result;
};

/**
 * Validates that all required data is present and period value is not null
 * @param extractedData - Data extracted from calendar entry
 * @returns True if data is valid for processing
 */
const isValidPeriodData = (extractedData: ExtractedColumnData): boolean => {
    const { periodValue, firstDay, lastDay } = extractedData;

    if (!periodValue || !firstDay || !lastDay) {
        return false;
    }

    // Check if period value is not null when parsed
    const parsedPeriodValue = parseJsonValue(periodValue);
    return parsedPeriodValue !== undefined;
};

/**
 * Processes calendar data to create fiscal period blocks
 * @param calendarData - Array of calendar data entries
 * @returns Array of fiscal period blocks
 */
const createFiscalPeriodBlocks = (calendarData: CalendarDataEntry[][], periodUnit: PeriodUnit): FiscalPeriodBlock[] => {
    const blocks: FiscalPeriodBlock[] = [];

    for (const dataEntry of calendarData) {
        const extractedData = extractColumnData(dataEntry, periodUnit);

        // Skip invalid entries early
        if (!isValidPeriodData(extractedData)) {
            continue;
        }

        // Parse dates with null checks
        const startDate = parseDateFromJson(extractedData.firstDay!);
        const endDate = parseDateFromJson(extractedData.lastDay!);

        if (!startDate || !endDate) {
            continue;
        }

        // Check if this period continues the previous one
        const lastBlock = blocks[blocks.length - 1];
        if (lastBlock && lastBlock.value === extractedData.periodValue) {
            // Extend existing period
            lastBlock.length++;
            lastBlock.endDate = endDate;
        } else {
            // Create new period block
            blocks.push({
                value: extractedData.periodValue!,
                length: 1,
                startDate,
                endDate
            });
        }
    }

    return blocks;
};

/**
 * Converts fiscal period blocks to Period objects within the specified date range
 * @param blocks - Array of fiscal period blocks
 * @param startDate - Filter start date
 * @param endDate - Filter end date
 * @returns Array of periods within date range
 */
const convertBlocksToPeriods = (
    blocks: FiscalPeriodBlock[],
    startDate: Date,
    endDate: Date
): Period[] => {
    return blocks
        .filter(block => block.startDate >= startDate && block.endDate <= endDate)
        .map(block => ({
            startDate: block.startDate,
            endDate: block.endDate
        }));
};

/**
 * Retrieves fiscal periods from calendar data within the specified date range
 * 
 * This method processes calendar data to extract fiscal periods based on the period6_text column,
 * grouping consecutive weeks that belong to the same fiscal period and returning only those
 * periods that fall within the specified date range.
 * 
 * @param calendarHelper - Helper containing calendar data
 * @param startDate - Start date for filtering periods (inclusive)
 * @param endDate - End date for filtering periods (inclusive)
 * @param periodUnit - Period unit type (kept for interface compatibility)
 * @returns Array of fiscal periods within the specified date range
 * 
 * @throws {Error} When start date is later than end date
 * @throws {Error} When calendarHelper is null or undefined
 * 
 * @example
 * ```typescript
 * const periods = getFiscalPeriods(
 *   calendarHelper, 
 *   new Date('2023-01-01'), 
 *   new Date('2023-12-31'), 
 *   PeriodUnit.FISCAL
 * );
 * console.log('Found ' + periods.length + ' fiscal periods');
 * ```
 */
export const getFiscalPeriods = (
    calendarHelper: CalendarHelper,
    startDate: Date,
    endDate: Date,
    periodUnit: PeriodUnit
): Period[] => {
    // Input validation
    if (!calendarHelper) {
        throw new Error('CalendarHelper cannot be null or undefined');
    }

    if (!calendarHelper.data || !calendarHelper.data.length) {
        return [];
    }

    // Validate date range
    if (startDate > endDate) {
        throw new Error('Start date cannot be later than end date');
    }

    try {
        const fiscalBlocks = createFiscalPeriodBlocks(calendarHelper.data, periodUnit);
        return convertBlocksToPeriods(fiscalBlocks, startDate, endDate);
    } catch (error) {
        console.error('Error processing fiscal periods:', error);
        throw new Error('Failed to process fiscal periods: ' + (error as Error).message);
    }
};